import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import axios from 'axios';

const TradeTemplates = ({ onApplyTemplate }) => {
  const [templates, setTemplates] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'stock',
    direction: '',
    setup: '',
    notes: '',
    tags: [],
    strategyIds: []
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/tradeTemplates', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setError('Failed to load templates');
    }
  };

  const handleOpenDialog = (template = null) => {
    if (template) {
      setCurrentTemplate(template);
      setFormData({
        name: template.name || '',
        description: template.description || '',
        category: template.category || 'stock',
        direction: template.direction || '',
        setup: template.setup || '',
        notes: template.notes || '',
        tags: template.tags || [],
        strategyIds: template.strategyIds || []
      });
    } else {
      setCurrentTemplate(null);
      setFormData({
        name: '',
        description: '',
        category: 'stock',
        direction: '',
        setup: '',
        notes: '',
        tags: [],
        strategyIds: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
    setSuccess('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTagsChange = (e) => {
    const tags = e.target.value.split(',').map(tag => tag.trim()).filter(Boolean);
    setFormData(prev => ({ ...prev, tags }));
  };

  const handleSaveTemplate = async () => {
    try {
      if (!formData.name) {
        setError('Template name is required');
        return;
      }

      const token = localStorage.getItem('idToken');
      const payload = {
        ...formData,
        templateId: currentTemplate?.templateId
      };

      const response = await axios.post(
        'https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/tradeTemplates',
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(currentTemplate ? 'Template updated successfully' : 'Template created successfully');
      fetchTemplates();
      
      // Close dialog after a short delay to show success message
      setTimeout(() => {
        handleCloseDialog();
      }, 1500);
    } catch (error) {
      console.error('Error saving template:', error);
      setError('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      const token = localStorage.getItem('idToken');
      await axios.delete(
        `https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/tradeTemplates/${templateId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      setError('Failed to delete template');
    }
  };

  const handleApplyTemplate = (template) => {
    if (onApplyTemplate) {
      onApplyTemplate(template);
    }
  };

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
            Trade Templates
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create Template
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {templates.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No templates found. Create your first template to speed up trade entry.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {templates.map((template) => (
              <Grid item xs={12} sm={6} md={4} key={template.templateId}>
                <Card elevation={1} sx={{ 
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="h6" gutterBottom>
                        {template.name}
                      </Typography>
                      <Box>
                        <Tooltip title="Edit Template">
                          <IconButton size="small" onClick={() => handleOpenDialog(template)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Template">
                          <IconButton size="small" onClick={() => handleDeleteTemplate(template.templateId)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {template.description || 'No description'}
                    </Typography>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Category
                        </Typography>
                        <Typography variant="body2">
                          {template.category || 'Not set'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Direction
                        </Typography>
                        <Typography variant="body2">
                          {template.direction || 'Not set'}
                        </Typography>
                      </Grid>
                    </Grid>
                    
                    <Typography variant="caption" color="text.secondary">
                      Setup
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {template.setup || 'Not set'}
                    </Typography>
                    
                    {template.tags && template.tags.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Tags
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {template.tags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<CopyIcon />}
                      onClick={() => handleApplyTemplate(template)}
                      sx={{ mt: 1 }}
                    >
                      Apply Template
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Create/Edit Template Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentTemplate ? 'Edit Template' : 'Create Template'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="name"
                label="Template Name"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="category"
                label="Category"
                value={formData.category}
                onChange={handleInputChange}
                select
                fullWidth
                SelectProps={{
                  native: true
                }}
              >
                <option value="stock">Stock</option>
                <option value="crypto">Crypto</option>
                <option value="forex">Forex</option>
                <option value="futures">Futures</option>
                <option value="options">Options</option>
              </TextField>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="direction"
                label="Direction"
                value={formData.direction}
                onChange={handleInputChange}
                select
                fullWidth
                SelectProps={{
                  native: true
                }}
              >
                <option value="">Select Direction</option>
                <option value="LONG">Long</option>
                <option value="SHORT">Short</option>
              </TextField>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="setup"
                label="Setup"
                value={formData.setup}
                onChange={handleInputChange}
                fullWidth
                placeholder="e.g., Bull Flag, Support Bounce"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Notes & Instructions"
                value={formData.notes}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={3}
                placeholder="Add any notes or instructions for this template"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="tags"
                label="Tags (comma separated)"
                value={formData.tags.join(', ')}
                onChange={handleTagsChange}
                fullWidth
                placeholder="e.g., Breakout, Momentum, Reversal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveTemplate} 
            variant="contained" 
            startIcon={<SaveIcon />}
          >
            {currentTemplate ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TradeTemplates;